import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useInquiry } from '../../context/InquiryContext';
import { useAuth } from '../../context/AuthContext';
import { canSendRFQ } from '../../utils/membershipUtils';
import VerificationBadge from '../../components/VerificationBadge/VerificationBadge';
import { API_BASE_URL } from '../../config/api';
import './SendInquirePage.css';
import { LOGO_PLACEHOLDER } from '../../utils/placeholders';

const SendInquirePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { product, seller } = location.state || {};
  const { user } = useAuth();
  
  // Get inquiry items from context
  const { inquiryItems, clearInquiry } = useInquiry();
  
  // Use product from navigation state if no inquiry items
  const displayItems = useMemo(() => {
    if (inquiryItems.length > 0) {
      return inquiryItems;
    }
    // If product came from navigation state (Request for Inquire button)
    if (product) {
      return [{
        id: product.id,
        name: product.name || product.description,
        description: product.description || product.name,
        image: product.image,
        priceMin: product.priceMin,
        priceMax: product.priceMax,
        unit: product.unit,
        moq: product.moq,
        sellerId: product.sellerId || seller?.id,
        sellerName: seller?.name,
        companyName: seller?.name,
        sellerAvatar: seller?.logo,
        country: seller?.country,
        memberYears: seller?.memberYears,
        businessType: seller?.businessType
      }];
    }
    return [];
  }, [inquiryItems, product, seller]);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [formData, setFormData] = useState({
    quantity: '1',
    unit: 'Ton',
    price: '',
    message: '',
    email: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user can send RFQ - all plans allowed
    if (user && user.seller?.planName) {
      if (!canSendRFQ(user.seller.planName)) {
        alert('You cannot send RFQs. Please check your account permissions.');
        return;
      }
    }
    
    if (!formData.email) {
      alert('Please enter your email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Build product details from display items
      const productDetails = displayItems.length > 0 
        ? displayItems.map(item => `- ${item.description || item.name || 'Product'} (Qty: ${formData.quantity} ${formData.unit})`).join('\n')
        : `Product: ${product?.description || 'N/A'}`;
      
      const rfqData = {
        companyName: displayItems[0]?.companyName || seller?.name || 'Inquiry from Website',
        fullName: formData.email.split('@')[0] || 'Customer',
        email: formData.email,
        phoneNumber: 'N/A',
        country: displayItems[0]?.country || seller?.country || 'Not specified',
        category: product?.category?.name || product?.category || 'Building Materials',
        subCategory: product?.subcategory?.name || product?.subcategory || null,
        details: `Products:\n${productDetails}\n\nQuantity: ${formData.quantity} ${formData.unit}\nPrice Requested: ${formData.price || 'Not specified'}\n\nMessage: ${formData.message}`,
        quantity: parseInt(formData.quantity) || 1,
        unit: formData.unit,
        offerPrice: parseFloat(formData.price) || null,
        isPublicOffer: false,
        targetSellerId: displayItems[0]?.sellerId || seller?.id || null
      };

      const response = await fetch(`${API_BASE_URL}/rfq`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(rfqData)
      });

      if (response.ok) {
        setSubmitSuccess(true);
        // Clear the inquiry cart after successful submission
        clearInquiry();
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to send inquiry');
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      alert('Failed to send inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Use first display item's seller info or fallback to location state
  const firstItem = displayItems[0] || {};
  const sellerData = {
    name: firstItem.companyName || firstItem.sellerName || seller?.name || 'Unknown Seller',
    logo: firstItem.sellerAvatar || seller?.logo || LOGO_PLACEHOLDER,
    type: seller?.type || 'Global',
    verified: seller?.verified !== false,
    verificationLevel: seller?.verificationLevel || null,
    country: firstItem.country || seller?.country || 'UAE',
    memberYears: firstItem.memberYears || seller?.memberYears || 1,
    businessType: firstItem.businessType || seller?.businessType || 'Manufacturer'
  };

  // Check if there are no items in inquiry
  if (displayItems.length === 0) {
    return (
      <div className="send-inquire-page">
        <Header />
        <main className="send-inquire-main">
          <div className="send-inquire-container">
            <h1 className="send-inquire-title">Send Inquire</h1>
            <div className="no-products-message" style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ fontSize: '18px', color: '#666' }}>No products in your inquiry cart.</p>
              <button 
                onClick={() => navigate('/products')} 
                style={{ 
                  marginTop: '20px', 
                  padding: '12px 24px', 
                  backgroundColor: '#C8A165', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer' 
                }}
              >
                Browse Products
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="send-inquire-page">
      <Header />
      
      <main className="send-inquire-main">
        <div className="send-inquire-container">
          {/* Page Title */}
          <h1 className="send-inquire-title">Send Inquire</h1>

          {/* Single Column Layout */}
          <div className="send-inquire-single-column">
            {/* Seller Info Section */}
            <div className="send-inquire-seller-section">
              {/* Seller Info Card */}
              <div className="seller-info-card">
                <div className="seller-info-content">
                  <img 
                    src={sellerData.logo} 
                    alt={sellerData.name} 
                    className="seller-logo"
                  />
                  <div className="seller-details">
                    <h2 className="seller-name">{sellerData.name}</h2>
                    <div className="seller-badges">
                      <div className="badge-item">
                        <img 
                          src="/uploads/builder-images/050559d54da8d35ba6d11841efdb737d4c3ae660_w44.png" 
                          alt="Platinum" 
                          className="badge-icon"
                        />
                        <span className="badge-text">{sellerData.type}</span>
                      </div>
                      <VerificationBadge level={sellerData.verificationLevel || (sellerData.verified ? 'verified' : null)} size="md" />
                      <div className="location-badge">
                        <div className="country-flag">
                          <img 
                            src="/uploads/builder-images/de3c066e27bac66ba8734a78f507a4580d9a2cd8_w124.png" 
                            alt="Country Flag" 
                            className="flag-image"
                          />
                        </div>
                        <span className="location-text">
                          <span className="country-name">{sellerData.country}</span>
                          <span className="separator"> / </span>
                          <span className="member-years">{String(sellerData.memberYears).padStart(2, '0')} Year</span>
                        </span>
                      </div>
                      <div className="business-type-badge">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14.5 5H12V6H14.5V9H11V10H12.5V11.071C12.0287 11.1927 11.6179 11.4821 11.3447 11.885C11.0715 12.2879 10.9546 12.7766 11.0159 13.2595C11.0773 13.7424 11.3126 14.1864 11.6779 14.5082C12.0431 14.83 12.5132 15.0075 13 15.0075C13.4868 15.0075 13.9569 14.83 14.3221 14.5082C14.6874 14.1864 14.9227 13.7424 14.9841 13.2595C15.0454 12.7766 14.9285 12.2879 14.6553 11.885C14.3821 11.4821 13.9713 11.1927 13.5 11.071V10H14.5C14.7651 9.9996 15.0192 9.89412 15.2067 9.70667C15.3941 9.51922 15.4996 9.26509 15.5 9V6C15.5 5.73478 15.3946 5.48043 15.2071 5.29289C15.0196 5.10536 14.7652 5 14.5 5ZM14 13C14 13.1978 13.9414 13.3911 13.8315 13.5556C13.7216 13.72 13.5654 13.8482 13.3827 13.9239C13.2 13.9996 12.9989 14.0194 12.8049 13.9808C12.6109 13.9422 12.4327 13.847 12.2929 13.7071C12.153 13.5673 12.0578 13.3891 12.0192 13.1951C11.9806 13.0011 12.0004 12.8 12.0761 12.6173C12.1518 12.4346 12.28 12.2784 12.4444 12.1685C12.6089 12.0586 12.8022 12 13 12C13.2651 12.0004 13.5192 12.1059 13.7067 12.2933C13.8941 12.4808 13.9996 12.7349 14 13ZM9.5 3H7V4H9.5V7H6V8H7.5V11.071C7.02867 11.1927 6.61791 11.4821 6.3447 11.885C6.07149 12.2879 5.9546 12.7766 6.01594 13.2595C6.07728 13.7424 6.31263 14.1864 6.67788 14.5082C7.04314 14.83 7.51321 15.0075 8 15.0075C8.48679 15.0075 8.95686 14.83 9.32212 14.5082C9.68737 14.1864 9.92272 13.7424 9.98406 13.2595C10.0454 12.7766 9.92851 12.2879 9.6553 11.885C9.3821 11.4821 8.97133 11.1927 8.5 11.071V8H9.5C9.76522 8 10.0196 7.89464 10.2071 7.7071C10.3946 7.51957 10.5 7.26521 10.5 7V4C10.5 3.73478 10.3946 3.48043 10.2071 3.29289C10.0196 3.10536 9.76522 3 9.5 3ZM9 13C9 13.1978 8.94135 13.3911 8.83147 13.5556C8.72159 13.72 8.56541 13.8482 8.38268 13.9239C8.19996 13.9996 7.99889 14.0194 7.80491 13.9808C7.61093 13.9422 7.43275 13.847 7.29289 13.7071C7.15304 13.5673 7.0578 13.3891 7.01921 13.1951C6.98063 13.0011 7.00043 12.8 7.07612 12.6173C7.15181 12.4346 7.27998 12.2784 7.44443 12.1685C7.60888 12.0586 7.80222 12 8 12C8.26509 12.0004 8.51922 12.1059 8.70667 12.2933C8.89412 12.4808 8.9996 12.7349 9 13ZM4.5 1H1.5C1.23478 1 0.98043 1.10536 0.792893 1.29289C0.605357 1.48043 0.5 1.73478 0.5 2V5C0.5 5.26522 0.605357 5.51957 0.792893 5.70711C0.98043 5.89464 1.23478 6 1.5 6H2.5V11.071C2.02867 11.1927 1.61791 11.4821 1.3447 11.885C1.07149 12.2879 0.954603 12.7766 1.01594 13.2595C1.07728 13.7424 1.31263 14.1864 1.67788 14.5082C2.04314 14.83 2.51321 15.0075 3 15.0075C3.48679 15.0075 3.95686 14.83 4.32212 14.5082C4.68737 14.1864 4.92272 13.7424 4.98406 13.2595C5.0454 12.7766 4.92851 12.2879 4.6553 11.885C4.38209 11.4821 3.97133 11.1927 3.5 11.071V6H4.5C4.76522 6 5.01957 5.89464 5.20711 5.70711C5.39464 5.51957 5.5 5.26522 5.5 5V2C5.5 1.73478 5.39464 1.48043 5.20711 1.29289C5.01957 1.10536 4.76522 1 4.5 1ZM4 13C4 13.1978 3.94135 13.3911 3.83147 13.5556C3.72159 13.72 3.56541 13.8482 3.38268 13.9239C3.19996 13.9996 2.99889 14.0194 2.80491 13.9808C2.61093 13.9422 2.43275 13.847 2.29289 13.7071C2.15304 13.5673 2.0578 13.3891 2.01921 13.1951C1.98063 13.0011 2.00043 12.8 2.07612 12.6173C2.15181 12.4346 2.27998 12.2784 2.44443 12.1685C2.60888 12.0586 2.80222 12 3 12C3.26522 12 3.51957 12.1054 3.70711 12.2929C3.89464 12.4804 4 12.7348 4 13ZM1.5 5V2H4.5L4.501 5H1.5Z" fill="#6D6D6D"/>
                        </svg>
                        <span className="business-type-text">{sellerData.businessType}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Information Section */}
              <div className="product-info-section-simple">
                <h3 className="section-label-simple">Product Information</h3>
                {displayItems.map((item, index) => (
                  <div key={item.id || index} className="product-info-card-simple">
                    <img 
                      src={item.image || '/placeholder-product.png'} 
                      alt={item.name || 'Product'} 
                      className="product-image-simple"
                    />
                    <p className="product-description-simple">{item.name || item.description || 'Product'}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Section - Inline Form */}
            <div className="send-inquire-form-section">
              <form onSubmit={handleSubmit} className="inquiry-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Quantity</label>
                      <div className="input-with-dropdown">
                        <input
                          type="text"
                          name="quantity"
                          value={formData.quantity}
                          onChange={handleInputChange}
                          className="form-input"
                      placeholder="43"
                    />
                    <div className="unit-dropdown">
                      <span>{formData.unit}</span>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9L12 15L18 9" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Price</label>
                  <div className="input-with-icon">
                    <input
                      type="text"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Price"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group message-group">
                <label className="form-label">Message to the supplier</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="form-textarea"
                  placeholder="Write a short description"
                  rows="5"
                />
              </div>

              <div className="form-group email-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input email-input"
                  placeholder="Email"
                  required
                />
              </div>

              {submitSuccess && (
                <div className="success-message">
                  ✓ Inquiry sent successfully! Redirecting...
                </div>
              )}

              <button 
                type="submit" 
                className={`submit-button ${isSubmitting ? 'loading' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Inquire'}
              </button>
            </form>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SendInquirePage;
